import { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest, getApiBaseUrl, getStoredToken } from '../services/apiClient';

const FALLBACK_POLL_MS = 5000;

const applyDurumPayload = (data, setters) => {
  const { setApiAktif, setKuyruklar, setHata, setSonGuncelleme } = setters;
  setApiAktif(data.ok !== false);
  setKuyruklar(data.kuyruklar || []);
  setHata(data.ok === false ? data.mesaj : (data.hata || null));
  setSonGuncelleme(data.sonGuncelleme || new Date().toISOString());
};

export function useKuyrukDurumu({ aktif = true } = {}) {
  const [kuyruklar, setKuyruklar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [sonGuncelleme, setSonGuncelleme] = useState(null);
  const [apiAktif, setApiAktif] = useState(null);

  const esRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const sseFailedRef = useRef(false);

  const settersRef = useRef({ setApiAktif, setKuyruklar, setHata, setSonGuncelleme });
  settersRef.current = { setApiAktif, setKuyruklar, setHata, setSonGuncelleme };

  const getir = useCallback(async () => {
    if (!mountedRef.current) return;
    setYukleniyor(true);
    try {
      const data = await apiRequest('/api/santral/durum', { method: 'GET', skipCsrf: true });
      if (!mountedRef.current) return;
      applyDurumPayload(data, settersRef.current);
    } catch (err) {
      if (!mountedRef.current) return;
      setHata(err?.message || 'Kuyruk verisi alınamadı');
    } finally {
      if (mountedRef.current) setYukleniyor(false);
    }
  }, []);

  const startFallbackPolling = useCallback(() => {
    if (fallbackTimerRef.current) return;
    getir();
    fallbackTimerRef.current = setInterval(getir, FALLBACK_POLL_MS);
  }, [getir]);

  const stopFallbackPolling = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearInterval(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const closeStream = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!aktif) {
      closeStream();
      stopFallbackPolling();
      return undefined;
    }

    sseFailedRef.current = false;
    setYukleniyor(true);

    const token = getStoredToken();
    if (!token || typeof EventSource === 'undefined') {
      startFallbackPolling();
      return () => {
        mountedRef.current = false;
        stopFallbackPolling();
      };
    }

    const url = `${getApiBaseUrl()}/api/santral/durum/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (event) => {
      if (!mountedRef.current) return;
      try {
        const data = JSON.parse(event.data);
        applyDurumPayload(data, settersRef.current);
        setYukleniyor(false);
      } catch {
        // geçersiz SSE paketi — yoksay
      }
    };

    es.onerror = () => {
      if (!mountedRef.current || sseFailedRef.current) return;
      sseFailedRef.current = true;
      closeStream();
      startFallbackPolling();
    };

    return () => {
      mountedRef.current = false;
      closeStream();
      stopFallbackPolling();
    };
  }, [aktif, closeStream, startFallbackPolling, stopFallbackPolling]);

  // Bekleme sürelerini SSE aralığında da saniye saniye ilerlet
  useEffect(() => {
    if (!aktif) return undefined;
    const tick = setInterval(() => {
      setKuyruklar((prev) => {
        const hasCallers = prev.some((k) => k.beklemedeCallerlar?.length > 0);
        if (!hasCallers) return prev;
        return prev.map((k) => ({
          ...k,
          beklemedeCallerlar: (k.beklemedeCallerlar || []).map((c) => ({
            ...c,
            bekleme: (c.bekleme || 0) + 1,
          })),
        }));
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [aktif]);

  return { kuyruklar, yukleniyor, hata, sonGuncelleme, apiAktif, yenile: getir };
}

export default useKuyrukDurumu;
